/**
 * El armazón de una vista. **Todas las pantallas se montan con esto.**
 *
 * Antes cada vista repetía a mano la misma estructura —un `<div>` con una clase
 * de una lista que no estaba escrita en ninguna parte, un `PageHeader`, y a
 * veces un `Breadcrumb`— en quince sitios. Nada impedía olvidar la clase, poner
 * dos, o inventar una tercera; y eso fue exactamente lo que pasó.
 *
 * ── Sobre el ancho ─────────────────────────────────────────────────────────
 *
 * Por defecto **no se declara ninguno**, y es deliberado: el `AppShell` del
 * design system ya lo resuelve en su `<main>`
 *
 *     .mpfn-main { width: min(100%, var(--mpfn-layout-content-max)); margin: auto }
 *
 * Declarar aquí otro tope sería una segunda capa sobre una decisión ya tomada
 * —ya ocurrió, ver AUDITORIA_DISPOSICION_Y_TRACKING.md §0.1—. La única
 * excepción es `ancho="formulario"`, para una columna de campos que a 1600 px
 * quedaría ilegible.
 *
 * Si hiciera falta que TODAS las vistas fueran más anchas, se sube
 * `--mpfn-layout-content-max`, que es el token del sistema.
 */
import type { ReactNode } from 'react'
import { PageHeader } from '@mpfn/patterns'
import { Breadcrumb, type Miga } from './Breadcrumb.js'

interface PropsPagina {
  /**
   * Si se da, `Pagina` monta el `PageHeader`.
   *
   * Se omite cuando el título depende de datos que todavía no han llegado —el
   * bloque muestra su período, el panel su recuento— y entonces la vista monta
   * su propio `PageHeader` dentro del `Datos`. Sigue mereciendo la pena usar
   * `Pagina`: aporta el contenedor, la separación, el ancho y las migas, que es
   * lo que de verdad se estaba duplicando.
   */
  titulo?: string
  descripcion?: string
  /** Acción principal de la pantalla, a la derecha del título. */
  accion?: ReactNode
  /** Migas de pan. Solo las vistas de detalle las necesitan. */
  migas?: readonly Miga[]
  /**
   * `formulario` para una sola columna de campos. Ausente para todo lo demás,
   * que es lo correcto en trece de las quince vistas.
   */
  ancho?: 'formulario'
  /** Clases extra de la vista. Hoy solo la usa la bandeja del notificador. */
  className?: string
  children: ReactNode
}

export function Pagina({
  titulo,
  descripcion,
  accion,
  migas,
  ancho,
  className,
  children,
}: PropsPagina) {
  const clases = [
    'sigfisc-vista',
    ancho === 'formulario' ? 'sigfisc-vista--formulario' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={clases}>
      {migas && migas.length > 0 && <Breadcrumb migas={migas} />}
      {titulo !== undefined && (
        <PageHeader
          title={titulo}
          {...(descripcion !== undefined ? { description: descripcion } : {})}
          {...(accion !== undefined ? { action: accion } : {})}
        />
      )}
      {children}
    </div>
  )
}
